import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/Kpi";

const inp = "border border-slate-200 rounded-md px-3 py-2 text-sm bg-white " +
  "focus:outline-none focus:ring-2 focus:ring-slate-300 transition-colors duration-200";

function Spunte({ voci, selezionati, onToggle, prefix }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-1.5">
      {voci.map((v) => (
        <label key={v.key} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <input type="checkbox" checked={selezionati.includes(v.key)} data-testid={`${prefix}-${v.key}`}
            onChange={() => onToggle(v.key)}
            className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-400" />
          <span className="truncate" title={v.label}>{v.label}</span>
        </label>
      ))}
    </div>
  );
}

/** Tipologie di utenza con autorizzazioni predefinite (es. EDITOR). */
export function TipologieUtenza({ pagine, funzioni, onChange }) {
  const [ruoli, setRuoli] = useState([]);
  const [nuovo, setNuovo] = useState({ label: "", pagine: [], funzioni: [] });
  const [aperto, setAperto] = useState(null);

  const carica = () => api.get("/ruoli").then((r) => {
    setRuoli(r.data.ruoli || []);
    onChange?.();
  }).catch(() => setRuoli([]));

  useEffect(() => { carica(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const crea = async () => {
    try {
      await api.post("/ruoli", nuovo);
      setNuovo({ label: "", pagine: [], funzioni: [] });
      await carica();
      toast.success("Tipologia creata");
    } catch (e) { toast.error(apiError(e)); }
  };

  const salva = async (r) => {
    try {
      await api.put(`/ruoli/${r.key}`, { label: r.label, pagine: r.pagine, funzioni: r.funzioni });
      await carica();
      toast.success("Autorizzazioni predefinite aggiornate");
    } catch (e) { toast.error(apiError(e)); }
  };

  const elimina = async (r) => {
    try {
      await api.delete(`/ruoli/${r.key}`);
      await carica();
      toast.success("Tipologia eliminata");
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleLocale = (r, campo, key) => setRuoli(ruoli.map((x) => x.key === r.key
    ? { ...x, [campo]: x[campo].includes(key) ? x[campo].filter((k) => k !== key) : [...x[campo], key] }
    : x));

  return (
    <Card className="p-6 mb-8" data-testid="tipologie-utenza">
      <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-1">
        Tipologie di utenza
      </h3>
      <p className="text-sm text-slate-500 mb-5">
        Le autorizzazioni salvate qui vengono applicate automaticamente quando assegni la tipologia
        a un utente, e restano modificabili sul singolo utente.
      </p>

      <div className="flex flex-wrap items-end gap-3 mb-6">
        <input className={`${inp} min-w-[240px]`} placeholder="Nome tipologia (es. Editor)"
          value={nuovo.label} data-testid="ruolo-nuovo-nome"
          onChange={(e) => setNuovo({ ...nuovo, label: e.target.value })} />
        <button onClick={crea} disabled={!nuovo.label.trim()} data-testid="ruolo-crea"
          className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm hover:bg-slate-800
            disabled:opacity-40 transition-colors duration-200">
          Crea tipologia
        </button>
      </div>

      <div className="space-y-2">
        {ruoli.map((r) => (
          <div key={r.key} className="border border-slate-200 rounded-md" data-testid={`ruolo-${r.key}`}>
            <button type="button" onClick={() => setAperto(aperto === r.key ? null : r.key)}
              data-testid={`ruolo-toggle-${r.key}`}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors duration-150">
              <span className="font-mono text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">{r.key}</span>
              <span className="text-sm font-medium">{r.label}</span>
              <span className="ml-auto text-xs text-slate-400">
                {r.pagine.length} pagine · {r.funzioni.length} autorizzazioni
              </span>
            </button>
            {aperto === r.key && (
              <div className="px-4 pb-4 space-y-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-slate-400 mb-2">Pagine visibili</div>
                  <Spunte voci={pagine} selezionati={r.pagine} prefix={`ruolo-pag-${r.key}`}
                    onToggle={(k) => toggleLocale(r, "pagine", k)} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-slate-400 mb-2">Autorizzazioni</div>
                  <Spunte voci={funzioni} selezionati={r.funzioni} prefix={`ruolo-fun-${r.key}`}
                    onToggle={(k) => toggleLocale(r, "funzioni", k)} />
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => salva(r)} data-testid={`ruolo-salva-${r.key}`}
                    className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm hover:bg-slate-800 transition-colors duration-200">
                    Salva predefiniti
                  </button>
                  <button onClick={() => elimina(r)} data-testid={`ruolo-elimina-${r.key}`}
                    className="text-slate-400 hover:text-red-600 transition-colors duration-150">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        {!ruoli.length && (
          <div className="text-sm text-slate-400">Nessuna tipologia personalizzata.</div>
        )}
      </div>
    </Card>
  );
}
